import { claudeCost } from "./ai-models";

/**
 * Agente de código com a chave de IA da própria pessoa (ADR 0025). O app desktop guarda a chave
 * no cofre do sistema e fala com o provedor; a página monta o pedido, roda as ferramentas
 * (arquivos do projeto, comandos, Git) e pede permissão antes de cada ação que muda algo.
 * Este arquivo é o contrato entre os dois lados, neutro em relação ao provedor.
 */

export const ASSISTANT_PROVIDER_IDS = [
  "anthropic",
  "openai",
  "google",
  "openrouter",
  "groq",
  "deepseek",
  "mistral",
  "xai",
  "ollama",
  "custom",
] as const;
export type AssistantProviderId = (typeof ASSISTANT_PROVIDER_IDS)[number];
export const isAssistantProvider = (v: unknown): v is AssistantProviderId =>
  ASSISTANT_PROVIDER_IDS.includes(v as AssistantProviderId);

export interface AssistantProviderInfo {
  id: AssistantProviderId;
  name: string;
  /** Endereço da API (compatível com a da OpenAI, menos a Anthropic). Vazio: a pessoa informa. */
  baseUrl: string;
  /** Onde criar a chave. */
  keyUrl?: string;
  keyPlaceholder: string;
  needsKey: boolean;
  /** Modelo sugerido quando a lista do provedor não vem. */
  defaultModel?: string;
}

export const ASSISTANT_PROVIDERS: readonly AssistantProviderInfo[] = [
  {
    id: "anthropic",
    name: "Anthropic (Claude)",
    baseUrl: "https://api.anthropic.com",
    keyUrl: "https://console.anthropic.com/settings/keys",
    keyPlaceholder: "sk-ant-…",
    needsKey: true,
    defaultModel: "claude-opus-5-5",
  },
  {
    id: "openai",
    name: "OpenAI",
    baseUrl: "https://api.openai.com/v1",
    keyUrl: "https://platform.openai.com/api-keys",
    keyPlaceholder: "sk-…",
    needsKey: true,
  },
  {
    id: "google",
    name: "Google (Gemini)",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai",
    keyUrl: "https://aistudio.google.com/apikey",
    keyPlaceholder: "AIza…",
    needsKey: true,
  },
  {
    id: "openrouter",
    name: "OpenRouter",
    baseUrl: "https://openrouter.ai/api/v1",
    keyUrl: "https://openrouter.ai/keys",
    keyPlaceholder: "sk-or-…",
    needsKey: true,
  },
  {
    id: "groq",
    name: "Groq",
    baseUrl: "https://api.groq.com/openai/v1",
    keyUrl: "https://console.groq.com/keys",
    keyPlaceholder: "gsk_…",
    needsKey: true,
  },
  {
    id: "deepseek",
    name: "DeepSeek",
    baseUrl: "https://api.deepseek.com/v1",
    keyUrl: "https://platform.deepseek.com/api_keys",
    keyPlaceholder: "sk-…",
    needsKey: true,
  },
  {
    id: "mistral",
    name: "Mistral",
    baseUrl: "https://api.mistral.ai/v1",
    keyUrl: "https://console.mistral.ai/api-keys",
    keyPlaceholder: "…",
    needsKey: true,
  },
  {
    id: "xai",
    name: "xAI (Grok)",
    baseUrl: "https://api.x.ai/v1",
    keyUrl: "https://console.x.ai",
    keyPlaceholder: "xai-…",
    needsKey: true,
  },
  {
    id: "ollama",
    name: "Ollama (no seu computador)",
    baseUrl: "http://localhost:11434/v1",
    keyPlaceholder: "",
    needsKey: false,
  },
  {
    id: "custom",
    name: "Outro compatível com OpenAI",
    baseUrl: "",
    keyPlaceholder: "Chave (se o servidor pedir)",
    needsKey: false,
  },
];

export const assistantProvider = (id: AssistantProviderId) => ASSISTANT_PROVIDERS.find((p) => p.id === id)!;

/** Provedores que usam chave (os que a detecção automática testa). */
export const KEY_PROVIDERS: readonly AssistantProviderId[] = ASSISTANT_PROVIDERS.filter(
  (p) => p.needsKey,
).map((p) => p.id);

/**
 * De qual provedor a chave parece ser, pelo formato (o mais provável primeiro). A confirmação é
 * sempre do próprio provedor (a chave é testada); sem formato conhecido, testa todos.
 */
export function keyCandidates(raw: string): AssistantProviderId[] {
  const key = raw.trim();
  if (key.startsWith("sk-ant-")) return ["anthropic"];
  if (key.startsWith("sk-or-")) return ["openrouter"];
  if (key.startsWith("gsk_")) return ["groq"];
  if (key.startsWith("xai-")) return ["xai"];
  if (key.startsWith("AIza")) return ["google"];
  if (/^sk-(proj|svcacct|admin)-/.test(key)) return ["openai"];
  // "sk-" genérico: OpenAI (chaves antigas) ou DeepSeek (32 hexadecimais).
  if (/^sk-[0-9a-f]{32}$/.test(key)) return ["deepseek", "openai"];
  if (key.startsWith("sk-")) return ["openai", "deepseek"];
  // Mistral: 32 letras e números, sem prefixo.
  if (/^[A-Za-z0-9]{32}$/.test(key)) return ["mistral", ...KEY_PROVIDERS.filter((p) => p !== "mistral")];
  return [...KEY_PROVIDERS];
}

/**
 * Confere se o texto colado tem formato de chave de API antes de qualquer coisa (nada de URL,
 * JSON, frases ou caracteres de controle chegando aos provedores ou ao cofre do sistema).
 */
export function checkApiKey(raw: unknown): { ok: true; key: string } | { ok: false; error: string } {
  if (typeof raw !== "string") return { ok: false, error: "Cole a chave da API." };
  const key = raw.trim().replace(/^["']|["']$/g, "");
  if (!key) return { ok: false, error: "Cole a chave da API." };
  if (/\s/.test(key))
    return { ok: false, error: "Isso não é uma chave de API: chaves não têm espaços nem quebras de linha." };
  if (/^https?:\/\//i.test(key) || /[{}[\]<>"'`\\]/.test(key))
    return { ok: false, error: "Isso não é uma chave de API. Cole só a chave (ex.: sk-ant-…, sk-…, AIza…)." };
  if (key.length < 20) return { ok: false, error: "Chave curta demais: confira se copiou ela inteira." };
  if (key.length > 300) return { ok: false, error: "Isso não parece uma chave de API (longa demais)." };
  if (!/^[A-Za-z0-9_\-.:=+/]+$/.test(key))
    return { ok: false, error: "Isso não é uma chave de API: ela tem caracteres que nenhuma chave usa." };
  return { ok: true, key };
}

/** Resultado da detecção: o provedor que aceitou a chave e alguns modelos que ela enxerga. */
export type AssistantDetectResult =
  | {
      ok: true;
      provider: AssistantProviderId;
      models: string[];
      total: number;
      /** Todos os modelos que servem ao agente (para a pessoa escolher o dela na conexão). */
      ids?: string[];
    }
  | { ok: false; error: string };

/**
 * Endereço aceito para um provedor: HTTPS, ou HTTP só no próprio computador (a chave nunca vai
 * em texto aberto pela rede).
 */
export function isSafeBaseUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.username || url.password || url.search || url.hash) return false;
  if (url.protocol === "https:") return true;
  return url.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
}

// ───────────── Conversa (neutra em relação ao provedor) ─────────────

export type AssistantBlock =
  | { type: "text"; text: string }
  | { type: "tool_use"; id: string; name: string; input: Record<string, unknown> }
  | { type: "tool_result"; toolUseId: string; content: string; isError?: boolean };

export interface AssistantMessage {
  role: "user" | "assistant";
  content: AssistantBlock[];
  /**
   * Resposta no formato do provedor que a gerou (ex.: blocos de raciocínio assinados da Anthropic),
   * devolvida sem mudar nos turnos seguintes. Outro provedor usa só `content`.
   */
  raw?: { provider: AssistantProviderId; data: unknown };
}

export interface AssistantToolDef {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

export interface AssistantTurnRequest {
  requestId: string;
  /** Conexão de IA usada (chave, endereço e modelo dela). Sem ela, a chave salva do provedor. */
  connectionId?: string;
  provider: AssistantProviderId;
  model: string;
  /** Quanto o modelo pensa antes de agir (já resolvido: nunca "auto"). */
  effort: AgentReasoning;
  /** Instruções fixas (vão em cache). */
  system: string;
  /** Contexto do projeto (árvore, arquivo aberto…), também em cache. */
  context: string;
  messages: AssistantMessage[];
  tools: AssistantToolDef[];
}

export type AssistantStopReason = "end_turn" | "tool_use" | "max_tokens" | "refusal" | "cancelled" | "other";

export interface AssistantUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  /** Custo exato (US$) quando o provedor informa (OpenRouter): vale mais que a estimativa. */
  costUsd?: number;
}

export type AssistantTurnResult =
  | {
      ok: true;
      message: AssistantMessage;
      stopReason: AssistantStopReason;
      model: string;
      usage?: AssistantUsage;
    }
  | {
      ok: false;
      error: string;
      auth?: boolean;
      /**
       * O modelo não está disponível agora (sem crédito, limite de uso, bloqueado para este app,
       * fora do ar): dá para trocar de modelo e tentar de novo.
       */
      unavailable?: boolean;
    };

/** Modelo escolhido automaticamente, testado na API do provedor. */
export type AssistantModelPick =
  | { ok: true; model: string; /** Por que este ("sem créditos: um grátis que respondeu"). */ reason: string }
  | { ok: false; error: string };

export type AssistantEvent =
  | { requestId: string; type: "text"; text: string }
  | { requestId: string; type: "thinking"; text: string }
  /** O modelo começou a montar uma chamada de ferramenta (a entrada ainda está chegando). */
  | { requestId: string; type: "tool"; id: string; name: string }
  /** Saída de um comando em execução. */
  | { requestId: string; type: "output"; text: string };

export interface AssistantExecRequest {
  requestId: string;
  projectId: string;
  command: string;
  timeoutMs: number;
}

export interface AssistantExecResult {
  exitCode: number | null;
  output: string;
  timedOut: boolean;
  cancelled: boolean;
  error?: string;
}

/**
 * Conexão de IA (Configurações › Conexões de IA): nome, provedor e o modelo confirmado. A chave fica
 * no cofre do sistema; aqui só o começo e o fim dela, ofuscados.
 */
export interface AssistantConnection {
  id: string;
  name: string;
  provider: AssistantProviderId;
  model: string;
  /** Modelos de texto da conta (trocar de modelo só entre estes). */
  models: string[];
  keyHint?: string;
  baseUrl?: string;
  createdAt: number;
}

/** O que a chave colada revela antes de salvar: a pessoa confere e confirma. */
export type AssistantInspectResult =
  | {
      ok: true;
      provider: AssistantProviderId;
      providerName: string;
      /** Só modelos de texto (conversa), recomendados para programar primeiro. */
      models: string[];
      recommended: string[];
      /** O modelo sugerido (o que a conta usa para programar). */
      model: string | null;
      keyHint?: string;
    }
  | { ok: false; error: string };

export interface AssistantProviderStatus {
  id: AssistantProviderId;
  connected: boolean;
  /** Começo e fim da chave, para a pessoa reconhecer. */
  hint?: string;
  baseUrl?: string;
}

export const ASSISTANT_LIMITS = {
  messages: 400,
  tools: 32,
  /** Tamanho do pedido inteiro (JSON). */
  requestChars: 6_000_000,
  commandChars: 4_000,
  /** Saída guardada de um comando (o fim, que é onde ficam os erros). */
  outputChars: 40_000,
  maxTimeoutMs: 15 * 60_000,
};

const ID = /^[A-Za-z0-9_-]{1,128}$/;
const TOOL_NAME = /^[a-z][a-z0-9_]{0,63}$/;
const MODEL = /^[A-Za-z0-9._:/@-]{1,200}$/;

function validBlock(b: unknown): b is AssistantBlock {
  if (!b || typeof b !== "object") return false;
  const x = b as Record<string, unknown>;
  if (x.type === "text") return typeof x.text === "string";
  if (x.type === "tool_use")
    return (
      typeof x.id === "string" &&
      ID.test(x.id) &&
      typeof x.name === "string" &&
      TOOL_NAME.test(x.name) &&
      !!x.input &&
      typeof x.input === "object" &&
      !Array.isArray(x.input)
    );
  if (x.type === "tool_result")
    return (
      typeof x.toolUseId === "string" &&
      ID.test(x.toolUseId) &&
      typeof x.content === "string" &&
      (x.isError === undefined || typeof x.isError === "boolean")
    );
  return false;
}

/** Confere o pedido vindo da página (formato, tamanhos e valores conhecidos). */
export function validTurnRequest(input: unknown): AssistantTurnRequest | null {
  if (!input || typeof input !== "object") return null;
  const r = input as Partial<AssistantTurnRequest>;
  if (typeof r.requestId !== "string" || !ID.test(r.requestId)) return null;
  if (r.connectionId !== undefined && (typeof r.connectionId !== "string" || !ID.test(r.connectionId)))
    return null;
  if (!isAssistantProvider(r.provider) || typeof r.model !== "string" || !MODEL.test(r.model)) return null;
  if (!isAgentReasoning(r.effort) || typeof r.system !== "string" || typeof r.context !== "string")
    return null;
  if (!Array.isArray(r.messages) || r.messages.length < 1 || r.messages.length > ASSISTANT_LIMITS.messages)
    return null;
  for (const m of r.messages) {
    if (!m || (m.role !== "user" && m.role !== "assistant") || !Array.isArray(m.content)) return null;
    if (!m.content.every(validBlock)) return null;
    if (m.raw !== undefined && (!m.raw || !isAssistantProvider(m.raw.provider))) return null;
  }
  if (r.messages[0]!.role !== "user") return null;
  if (!Array.isArray(r.tools) || r.tools.length > ASSISTANT_LIMITS.tools) return null;
  for (const t of r.tools) {
    if (!t || typeof t.name !== "string" || !TOOL_NAME.test(t.name) || typeof t.description !== "string")
      return null;
    if (!t.inputSchema || typeof t.inputSchema !== "object") return null;
  }
  try {
    if (JSON.stringify(r).length > ASSISTANT_LIMITS.requestChars) return null;
  } catch {
    return null;
  }
  return r as AssistantTurnRequest;
}

/** Modelos de conversa numa lista `/models` (tira embeddings, imagem, áudio, moderação…). */
export function chatModels(ids: string[]): string[] {
  return chatModelList(
    "custom",
    ids.map((id) => ({ id })),
  ).models;
}

/** O que a lista `/models` de um provedor diz de cada modelo (o que vier). */
export interface AssistantModelInfo {
  id: string;
  /** Aceita ferramentas (o agente precisa). Ausente: não informado. */
  tools?: boolean;
  /** Quando foi lançado (segundos), para achar o mais novo. */
  created?: number;
  /** US$ por milhão de tokens, quando o provedor informa (OpenRouter). */
  price?: { input: number; output: number };
}

export interface AssistantModelList {
  ok: boolean;
  /** Todos os que servem para o agente, em ordem alfabética. */
  models: string[];
  /** Os melhores para programar, na ordem; o primeiro é o padrão. */
  recommended?: string[];
  /** Preço informado pelo provedor (US$ por milhão de tokens). */
  prices?: Record<string, { input: number; output: number }>;
  error?: string;
}

/**
 * O agente conversa e edita código: só modelos de texto. Fora: embeddings, imagem, vídeo, áudio,
 * voz, moderação, busca e afins.
 */
const NOT_CHAT =
  /embed|tts|whisper|dall-e|davinci|babbage|moderation|image|audio|realtime|transcri|search|speech|rerank|guard|vision-preview|imagen|veo|aqa|\bsora\b|video|music|\blyria|\bkling\b|\brunway|\bflux\b|stable-diffusion|sdxl|midjourney|seedream|seedance|\bwan-|hunyuan-video|\bvoice\b|\bclip\b|\bocr\b/i;

/** O id é de um modelo de texto (conversa)? */
export const isTextModel = (id: string) => MODEL.test(id) && !NOT_CHAT.test(id);

/**
 * Modelos para programar, por provedor, do mais indicado para o menos: o começo do id. Em cada
 * um vale a versão mais nova, evitando as reduzidas (mini, lite…) e as gratuitas com fila.
 */
const PREFERRED: Partial<Record<AssistantProviderId, readonly string[]>> = {
  openrouter: [
    "anthropic/claude-sonnet",
    "openai/gpt-5",
    "google/gemini",
    "anthropic/claude-opus",
    "qwen/qwen3-coder",
    "deepseek/deepseek-chat",
    "x-ai/grok-code",
    "mistralai/devstral",
    "moonshotai/kimi",
  ],
  openai: ["gpt-5", "gpt-4.1", "o4", "gpt-4o", "o3"],
  google: ["gemini-2.5-pro", "gemini-2.5-flash", "gemini"],
  groq: ["moonshotai/kimi", "openai/gpt-oss-120b", "qwen/qwen3", "llama-3.3-70b", "llama"],
  deepseek: ["deepseek-chat", "deepseek-reasoner"],
  mistral: ["devstral", "codestral", "mistral-large", "mistral-medium"],
  xai: ["grok-code", "grok-4", "grok"],
  ollama: ["qwen3-coder", "qwen2.5-coder", "deepseek-coder", "devstral", "qwen", "llama"],
  custom: ["qwen3-coder", "qwen2.5-coder", "deepseek-coder", "devstral", "qwen", "llama"],
};

/** Versões que não são a principal da família (só valem se não houver outra). */
const SECONDARY = /mini|nano|lite|:free|preview|exp|pro\b|-pro|oss-20b|instruct|vision|tiny|small|thinking/i;

const versionCompare = (a: string, b: string) => b.localeCompare(a, "en", { numeric: true });

/**
 * A lista que o agente mostra: só modelos de conversa que aceitam ferramentas (as variantes
 * `:batch` do OpenRouter não respondem no chat), em ordem alfabética, mais os recomendados para
 * programar (o primeiro vira o padrão quando a pessoa ainda não escolheu) e os preços informados.
 */
export function chatModelList(
  provider: AssistantProviderId,
  infos: readonly AssistantModelInfo[],
): Required<Pick<AssistantModelList, "models" | "recommended" | "prices">> {
  const seen = new Map<string, AssistantModelInfo>();
  for (const info of infos) {
    const id = info.id.replace(/^models\//, "");
    if (!MODEL.test(id) || NOT_CHAT.test(id) || /:batch$/i.test(id) || info.tools === false) continue;
    if (!seen.has(id)) seen.set(id, { ...info, id });
  }
  const list = [...seen.values()];
  const models = list.map((m) => m.id).sort((a, b) => a.localeCompare(b));
  const recommended: string[] = [];
  for (const prefix of PREFERRED[provider] ?? []) {
    const matches = list.filter((m) => m.id.toLowerCase().startsWith(prefix) && !recommended.includes(m.id));
    if (!matches.length) continue;
    const rank = (m: AssistantModelInfo) => (SECONDARY.test(m.id.slice(prefix.length)) ? 1 : 0);
    matches.sort(
      (a, b) => rank(a) - rank(b) || (b.created ?? 0) - (a.created ?? 0) || versionCompare(a.id, b.id),
    );
    recommended.push(matches[0]!.id);
    if (recommended.length >= 6) break;
  }
  const prices: Record<string, { input: number; output: number }> = {};
  for (const m of list) if (m.price) prices[m.id] = m.price;
  return { models, recommended, prices };
}

/** O modelo padrão de uma lista: o primeiro recomendado ou, sem nenhum, o primeiro da lista. */
export const defaultAssistantModel = (list: Pick<AssistantModelList, "models" | "recommended">) =>
  list.recommended?.[0] ?? list.models[0];

// ───────────── Custo e limite de gasto ─────────────

/**
 * Esforço do agente: quanto o modelo pensa antes de agir. "Automático" decide por pedido (pergunta
 * curta: leve; mudança grande, bug ou arquitetura: alto). O controle de custo é o limite em US$.
 */
export const AGENT_EFFORTS = ["auto", "low", "medium", "high", "max"] as const;
export type AgentEffort = (typeof AGENT_EFFORTS)[number];
/** O esforço que vai no pedido ao modelo. */
export type AgentReasoning = Exclude<AgentEffort, "auto">;
export const isAgentEffort = (v: unknown): v is AgentEffort => AGENT_EFFORTS.includes(v as AgentEffort);
export const isAgentReasoning = (v: unknown): v is AgentReasoning => isAgentEffort(v) && v !== "auto";
export const AGENT_EFFORT_LABELS: Record<AgentEffort, string> = {
  auto: "Automático",
  low: "Leve",
  medium: "Médio",
  high: "Alto",
  max: "Muito alto",
};

const HARD =
  /refator|arquitet|migr|bug|erro|falha|quebr|crash|otimiz|desempenho|performance|seguran|vulner|teste|test|implement|integra|autentic|banco de dados|deploy|publica|sistema|app inteiro|do zero/i;
const ACTION =
  /cri[ae]|faç|fazer|adicion|mud[ae]|troc|corrij|corrig|escrev|remov|apag|instal|configur|atualiz|gere|gerar/i;

/** "Automático": pergunta curta pede pouco esforço; mudança grande ou difícil, mais. */
export function resolveEffort(choice: AgentEffort, task: string): AgentReasoning {
  if (choice !== "auto") return choice;
  const t = task.trim();
  if (HARD.test(t) || t.length > 700) return "high";
  if (t.length < 120 && !ACTION.test(t)) return "low";
  return "medium";
}

/**
 * Tamanho máximo de cada resposta do modelo (tokens). Sem isso, o OpenRouter reserva o máximo do
 * modelo (32–128 mil) e recusa quando o crédito não cobre a reserva, mesmo que a resposta real
 * fosse pequena. 16 mil cobre com folga um arquivo inteiro ou um passo do agente.
 */
export const AGENT_MAX_OUTPUT = 16_384;

/** US$ por milhão de tokens (entrada e saída). */
export interface Price {
  input: number;
  output: number;
}

/**
 * Preços públicos aproximados (US$/milhão de tokens) dos modelos mais usados, pelo começo do id
 * ("gpt-4.1-mini-2025…" casa com "gpt-4.1-mini"). É uma estimativa para o limite de gasto, não a
 * fatura: os provedores mudam preços e cobram detalhes (cache, lote) de jeitos diferentes.
 */
const MODEL_PRICES: ReadonlyArray<readonly [string, Price]> = [
  ["claude-opus", { input: 4, output: 20 }],
  ["claude-sonnet", { input: 2, output: 10 }],
  ["claude-haiku", { input: 0.1, output: 0.5 }],
  ["claude", { input: 3, output: 15 }],
  ["gpt-5-nano", { input: 0.05, output: 0.4 }],
  ["gpt-5-mini", { input: 0.25, output: 2 }],
  ["gpt-5", { input: 1.25, output: 10 }],
  ["gpt-4.1-nano", { input: 0.1, output: 0.4 }],
  ["gpt-4.1-mini", { input: 0.4, output: 1.6 }],
  ["gpt-4.1", { input: 2, output: 8 }],
  ["gpt-4o-mini", { input: 0.15, output: 0.6 }],
  ["gpt-4o", { input: 2.5, output: 10 }],
  ["gpt-", { input: 2.5, output: 10 }],
  ["o4-mini", { input: 1.1, output: 4.4 }],
  ["o3-mini", { input: 1.1, output: 4.4 }],
  ["o3", { input: 2, output: 8 }],
  ["o1-mini", { input: 1.1, output: 4.4 }],
  ["o1", { input: 15, output: 60 }],
  ["gemini-2.5-flash-lite", { input: 0.1, output: 0.4 }],
  ["gemini-2.5-flash", { input: 0.3, output: 2.5 }],
  ["gemini-2.5-pro", { input: 1.25, output: 10 }],
  ["gemini-2.0-flash", { input: 0.1, output: 0.4 }],
  ["gemini", { input: 1.25, output: 10 }],
  ["deepseek-reasoner", { input: 0.55, output: 2.19 }],
  ["deepseek-chat", { input: 0.27, output: 1.1 }],
  ["deepseek", { input: 0.55, output: 2.2 }],
  ["codestral", { input: 0.3, output: 0.9 }],
  ["devstral", { input: 0.1, output: 0.3 }],
  ["ministral", { input: 0.1, output: 0.1 }],
  ["mistral-small", { input: 0.1, output: 0.3 }],
  ["mistral-medium", { input: 0.4, output: 2 }],
  ["mistral-large", { input: 2, output: 6 }],
  ["mistral", { input: 2, output: 6 }],
  ["grok-code", { input: 0.2, output: 1.5 }],
  ["grok-3-mini", { input: 0.3, output: 0.5 }],
  ["grok", { input: 3, output: 15 }],
  ["llama", { input: 0.6, output: 0.8 }],
  ["meta-llama", { input: 0.6, output: 0.8 }],
  ["qwen", { input: 0.3, output: 1.2 }],
  ["kimi", { input: 0.6, output: 2.5 }],
  ["moonshot", { input: 0.6, output: 2.5 }],
];

/** Modelo desconhecido: o preço típico do provedor (conservador). Ollama roda no seu computador. */
const PROVIDER_PRICES: Record<AssistantProviderId, Price> = {
  anthropic: { input: 4, output: 20 },
  openai: { input: 2.5, output: 10 },
  google: { input: 1.25, output: 10 },
  openrouter: { input: 3, output: 15 },
  groq: { input: 0.6, output: 0.8 },
  deepseek: { input: 0.55, output: 2.2 },
  mistral: { input: 2, output: 6 },
  xai: { input: 3, output: 15 },
  ollama: { input: 0, output: 0 },
  custom: { input: 3, output: 15 },
};

/** Preço estimado de um modelo ("openai/gpt-4o" do OpenRouter também vale). */
export function assistantPrice(provider: AssistantProviderId, model: string): Price {
  if (provider === "ollama") return PROVIDER_PRICES.ollama;
  const id =
    model
      .toLowerCase()
      .replace(/^models\//, "")
      .split("/")
      .pop() ?? "";
  let best: Price | null = null;
  let size = 0;
  for (const [prefix, price] of MODEL_PRICES)
    if (id.startsWith(prefix) && prefix.length > size) {
      best = price;
      size = prefix.length;
    }
  return best ?? PROVIDER_PRICES[provider];
}

/**
 * Custo estimado (US$) de uma resposta. Claude usa a tabela exata do Kompil (com cache); os outros,
 * a tabela acima, com a leitura de cache a 25% e a gravação a 125% do preço de entrada.
 */
export function assistantCost(
  provider: AssistantProviderId,
  model: string,
  usage: AssistantUsage,
  /** Preço informado pelo provedor para este modelo (US$/milhão), se houver. */
  known?: Price,
): number {
  if (provider === "ollama") return 0;
  if (typeof usage.costUsd === "number" && Number.isFinite(usage.costUsd) && usage.costUsd >= 0)
    return usage.costUsd;
  const id = model.toLowerCase().split("/").pop() ?? "";
  if (id.startsWith("claude")) {
    const exact = claudeCost(id, usage);
    if (exact !== null) return exact;
  }
  // Modelo grátis (":free" do OpenRouter) não cobra.
  if (/:free$/i.test(model)) return 0;
  const p = known ?? assistantPrice(provider, model);
  return (
    (usage.inputTokens * p.input +
      usage.outputTokens * p.output +
      usage.cacheReadTokens * p.input * 0.25 +
      usage.cacheWriteTokens * p.input * 1.25) /
    1_000_000
  );
}

/** Situação do limite do mês: livre, perto (80%) ou atingido. */
export function spendStatus(spent: number, limit: number | null): "free" | "near" | "reached" {
  if (!limit || limit <= 0) return "free";
  if (spent >= limit) return "reached";
  return spent >= limit * 0.8 ? "near" : "free";
}
